import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Account_Promote, ModelError_Response } from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import AutocompleteAllUserAvailableCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteAllUserAvailableCompaniesTypeahead';
import { RadioGroupFormField } from '~components/FormFields/RadioGroupFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import {
  CompanyForm,
  CompanyFormHandler,
  CompanyFormPrefillData,
  CompanyFormValues,
} from '~pages/Admin/Companies/CompanyForm';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import {
  PROMOTE_TARGET_MODE,
  PromoteAccountDto,
  promoteAccountSchema,
} from './promoteAccountValidationSchema';

interface PromoteAccountDialogProps {
  account: Account | null;
  // The company the account list is scoped to. List responses can omit the
  // nested `account.company`, so the promote request MUST use this instead
  // (see AdminAccountsDataGrid's `selectedCompany`, which is always the
  // company these accounts were fetched under).
  companyId: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: (account: Account) => void;
}

const DEFAULT_VALUES: PromoteAccountDto = {
  mode: PROMOTE_TARGET_MODE.new,
  targetCompany: null,
  backfill: true,
  accountingId: '',
  externalId: '',
};

// The identity fields stamp onto the source (billing-identity) account, so they
// are prefilled from it; the rest of the form starts from the shared defaults.
const buildDefaultValues = (account: Account | null): PromoteAccountDto => ({
  ...DEFAULT_VALUES,
  accountingId: account?.accountingId ?? '',
  externalId: account?.externalId ?? '',
});

// Build the prefill shape CompanyForm reads from. There is no Company record yet
// (that's the point of promotion), so this uses CompanyFormPrefillData rather than
// forcing a partial object into the AdminCompany model class.
const buildPrefillCompany = (account: Account): CompanyFormPrefillData => {
  const { address, primaryContact, billingContact } = account;

  return {
    legalName: account.name,
    dbaName: account.name,
    companyType: null,
    address: address
      ? {
          streetAddress: address.streetAddress,
          premise: address.premise,
          locality: address.locality,
          administrativeArea: address.administrativeArea,
          postalCode: address.postalCode,
          country: address.country,
          lat: address.lat,
          lng: address.lng,
          placeId: address.placeId,
        }
      : undefined,
    primaryContact: primaryContact
      ? {
          name: primaryContact.name,
          email: primaryContact.email,
          phone: primaryContact.phone,
        }
      : undefined,
    billingContact: billingContact
      ? {
          name: billingContact.name,
          email: billingContact.email,
          phone: billingContact.phone,
        }
      : undefined,
  };
};

// Map the embedded CompanyForm values to the snake_case target_company payload
// the promote endpoint expects.
const buildTargetCompany = (
  values: CompanyFormValues,
): Account_Promote['target_company'] => ({
  legal_name: values.legalName,
  company_type: values.companyType ?? undefined,
  address: {
    thoroughfare: values.address.streetAddress,
    premise: values.address.premise || undefined,
    locality: values.address.locality,
    administrative_area: values.address.administrativeArea,
    postal_code: values.address.postalCode,
    country: values.address.country?.code ?? '',
    lat: values.address.lat !== null ? String(values.address.lat) : undefined,
    lon: values.address.lng !== null ? String(values.address.lng) : undefined,
    place_id: values.address.placeId || undefined,
  },
  // The shared serializer, not a local join: it normalizes the phone to E.164.
  // The prefill reads `ContactItem.phone`, which is display-formatted, so a
  // promote request that reused an untouched prefilled number failed the
  // backend's e164_phone validation.
  primary_contact: ContactItem.deparseCompany(values.primaryContact),
  billing_contact: ContactItem.deparseCompany(values.billingContact),
});

const PromoteAccountDialog = ({
  account,
  companyId,
  open,
  onClose,
  onSuccess,
}: PromoteAccountDialogProps) => {
  const { promoteAccount } = useAccount();
  const { toasterStore } = useStores();

  const [isLoading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const companyFormRef = useRef<CompanyFormHandler>(null);

  const { control, handleSubmit, watch, reset } = useForm<PromoteAccountDto>({
    resolver: yupResolver(promoteAccountSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: buildDefaultValues(account),
  });

  const mode = watch('mode');
  const isNewCompany = mode === PROMOTE_TARGET_MODE.new;

  // Stable identity across re-renders (e.g. the watch('mode') below firing on
  // every field change) so CompanyForm's defaultCompany effect only resets
  // the embedded form when the source account actually changes, not on every
  // keystroke elsewhere in this dialog.
  const prefillCompany = useMemo(
    () => (account ? buildPrefillCompany(account) : null),
    [account],
  );

  useEffect(() => {
    reset(buildDefaultValues(account));
    setServerErrors([]);
  }, [account, reset]);

  const handleClose = useCallback(() => {
    onClose();
    reset(buildDefaultValues(account));
    setServerErrors([]);
  }, [account, onClose, reset]);

  const submitPromotion = useCallback(
    async (body: Account_Promote) => {
      if (!account || !companyId) {
        return;
      }

      setIsLoading(true);
      setServerErrors([]);

      try {
        const promoted = await promoteAccount({ companyId, id: account.id, body });

        if (promoted) {
          toasterStore.push(
            alert(
              t('promote_account.promote_success', { name: promoted.name }),
              AlertTypes.success,
            ),
          );
          onSuccess(promoted);
          handleClose();
        }
      } catch (error) {
        // The hook re-throws 422 for form-level handling and already surfaces a
        // toast for other failures, so only field errors are mapped here.
        if (isAxiosError(error) && error.response?.status === 422) {
          const axiosError = error as AxiosError<ModelError_Response>;
          const apiErrors = axiosError.response?.data.error.errors ?? [];
          setServerErrors(apiErrors.map(({ message }) => message));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [account, companyId, promoteAccount, toasterStore, onSuccess, handleClose],
  );

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      // Blank identity fields are omitted (undefined, not null) so the backend
      // preserves any existing value on the source account (never clears it).
      const identityFields = {
        accounting_id: data.accountingId?.trim() || undefined,
        external_id: data.externalId?.trim() || undefined,
      };

      if (data.mode === PROMOTE_TARGET_MODE.new) {
        companyFormRef.current?.submit((companyValues) => {
          void submitPromotion({
            backfill: data.backfill,
            target_company: buildTargetCompany(companyValues),
            ...identityFields,
          });
        });
        return;
      }

      void submitPromotion({
        target_company_id: data.targetCompany?.value,
        backfill: data.backfill,
        ...identityFields,
      });
    })();
  }, [handleSubmit, submitPromotion]);

  return (
    <Dialog isOpen={open} onClose={handleClose}>
      <DialogHeader onClose={handleClose}>{t('promote_account.title')}</DialogHeader>

      <DialogContent>
        {account && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('promote_account.source_account')}
            </Typography>
            <Typography variant="body1">{account.name}</Typography>
            {account.company?.legalName && (
              <Typography variant="body2" color="text.secondary">
                {account.company.legalName}
              </Typography>
            )}
          </Box>
        )}

        <RadioGroupFormField
          control={control}
          errors={{}}
          label={t('promote_account.target_company')}
          name="mode"
          list={[
            {
              label: t('promote_account.create_new_company'),
              value: PROMOTE_TARGET_MODE.new,
            },
            {
              label: t('promote_account.use_existing_company'),
              value: PROMOTE_TARGET_MODE.existing,
            },
          ]}
        />

        {isNewCompany ? (
          <>
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('promote_account.create_new_company_notice')}
            </Alert>

            <CompanyForm
              ref={companyFormRef}
              defaultCompany={prefillCompany}
              editable
              isAdminPage
              requireContactEmail
              onChangeLogo={() => {}}
              onFormStateChange={() => {}}
            />
          </>
        ) : (
          <Box sx={{ mt: 1 }}>
            <AutocompleteAllUserAvailableCompaniesTypeahead
              control={control}
              isRequired
              label={t('form_fields.company')}
              name="targetCompany"
            />
          </Box>
        )}

        <Box sx={{ mt: 1 }}>
          <CheckBoxFormField
            control={control}
            label={t('promote_account.backfill_label')}
            name="backfill"
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <TextFormField
            control={control}
            dataUi="promote-account-accounting-id"
            label={t('form_fields.accounting_id')}
            name="accountingId"
          />
          <TextFormField
            control={control}
            dataUi="promote-account-external-id"
            label={t('form_fields.external_id')}
            name="externalId"
          />
        </Box>

        {serverErrors.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {serverErrors.map((message, index) => (
              <Typography key={`${message}-${index}`} color="error" variant="body2">
                {message}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <SmallButton variant="outlined" color="secondary" onClick={handleClose}>
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton loading={isLoading} onClick={onSubmit}>
          {t('promote_account.submit')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PromoteAccountDialog;
